import { createContext, useContext, ReactNode, useCallback } from 'react';
import { Spinner } from '@flixbus/honeycomb-react';

import { Voucher } from '../../types/Voucher';
import useQuery from '../../hooks/useQuery';
import { getVoucher } from '../../apis';

import styles from './voucher-detail.module.scss';

interface VoucherContextType {
  data: Voucher;
  isPending: boolean;
  error: Error | null;
  complete: boolean;
}

const VoucherContext = createContext<VoucherContextType | undefined>(undefined);

export const useVoucher = () => {
  const context = useContext(VoucherContext);
  if (!context) {
    throw new Error('useVoucher must be used within a VoucherProvider');
  }

  return context;
};

interface VoucherProviderProps {
  id: string;
  children: ReactNode;
  initialData?: Voucher;
}

const VoucherFetchProvider = ({ id, children }: { id: string; children: ReactNode }) => {
  const fetchVoucher = useCallback(() => getVoucher(id), [id]);
  const { data, error, isPending, complete } = useQuery(fetchVoucher);

  if (error) {
    return <div>Something went wrong 🥲</div>;
  }

  if (!data || isPending) {
    return <Spinner extraClasses={styles.spinner} size={'xl'} />;
  }

  return (
    <VoucherContext.Provider value={{ data, isPending, error, complete }}>
      {children}
    </VoucherContext.Provider>
  );
};

export const VoucherProvider = ({ id, children, initialData }: VoucherProviderProps) => {
  if (initialData) {
    return (
      <VoucherContext.Provider
        value={{ data: initialData, isPending: false, error: null, complete: true }}
      >
        {children}
      </VoucherContext.Provider>
    );
  }

  return <VoucherFetchProvider id={id}>{children}</VoucherFetchProvider>;
};
